import React, { useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  drawerAccordionSx,
  drawerAccordionSummarySx,
  outlinedFieldSx,
  sectionChipSx,
} from "../drawerSurfaceSx";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { useTranslation } from "react-i18next";

export default function EscandallosSection({
  escandallos,
  setEscandallos,
  escandalloGrupos,
  tiposProveedores,
  getSubgrupos,
  deleteEscandallo,
}) {
  const theme = useTheme();
  const { t } = useTranslation();
  const accSx = drawerAccordionSx(theme);
  const sumBase = drawerAccordionSummarySx(theme);
  const fin = outlinedFieldSx(theme);
  const chipSx = sectionChipSx(theme);

  // ----------------------------
  //  DIALOGO CONFIRMACIÓN
  // ----------------------------
  const [openDialog, setOpenDialog] = useState(false);
  const [pendingIndex, setPendingIndex] = useState(null);

  const askDelete = (index) => {
    setPendingIndex(index);
    setOpenDialog(true);
  };

  const confirmDelete = async () => {
    const i = pendingIndex;
    if (i === null) return;

    const esc = escandallos[i];

    // Backend delete if exists
    if (esc.ID_ESCANDALLO || esc.Escandallo_Id) {
      const id = esc.ID_ESCANDALLO || esc.Escandallo_Id;

      try {
        await deleteEscandallo(id);
      } catch (err) {
        console.error("Error deleting escandallo:", err);
        alert(t("Samples.deleteEscandalloError"));
        setOpenDialog(false);
        return;
      }
    }

    // Frontend update
    setEscandallos((prev) => prev.filter((_, idx) => idx !== i));

    setOpenDialog(false);
    setPendingIndex(null);
  };

  const cancelDelete = () => {
    setOpenDialog(false);
    setPendingIndex(null);
  };

  // ----------------------------
  //  LÓGICA DE ESTADO
  // ----------------------------
  const addLine = () => {
    setEscandallos((prev) => [...prev, {}]);
  };

  const update = (i, field, value) => {
    setEscandallos((prev) =>
      prev.map((esc, idx) => (idx === i ? { ...esc, [field]: value } : esc))
    );
  };

  return (
    <>
      <Accordion defaultExpanded={false} sx={accSx}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            ...sumBase,
            "& .MuiAccordionSummary-content": {
              ...sumBase["& .MuiAccordionSummary-content"],
              display: "flex",
              alignItems: "center",
            },
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: "0.92rem", mr: 0.5 }}>
            {t("Samples.escandallos")}
          </Typography>

          <Chip
            size="small"
            label={String(escandallos.length)}
            sx={{ ...chipSx, mr: 0.5 }}
            onClick={(e) => e.stopPropagation()}
          />

          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              addLine();
            }}
            sx={{ ml: 0.5 }}
            color="primary"
          >
            <AddCircleOutlineOutlinedIcon fontSize="small" />
          </IconButton>
        </AccordionSummary>

        <AccordionDetails sx={{ pt: 1.25, pb: 1.25 }}>
          {escandallos.map((esc, i) => (
            <Box
              key={i}
              display="flex"
              gap={1.5}
              alignItems="center"
              mb={1.5}
              sx={{ flexWrap: "wrap" }}
            >
              <FormControl size="small" sx={{ width: 160, ...fin }}>
                <InputLabel>{t("Samples.group")}</InputLabel>
                <Select
                  label={t("Samples.group")}
                  value={esc.ESCANDALLO_GRUPOS || ""}
                  sx={fin}
                  onChange={async (e) => {
                    const grupoId = e.target.value;

                    update(i, "ESCANDALLO_GRUPOS", grupoId);

                    const subgrupos = await getSubgrupos(grupoId);

                    setEscandallos((prev) =>
                      prev.map((item, idx) =>
                        idx === i
                          ? {
                              ...item,
                              SUBGRUPOS_LIST: subgrupos,
                              ESCANDALLO_SUBGRUPO: "",
                            }
                          : item
                      )
                    );
                  }}
                >
                  {escandalloGrupos.map((g) => (
                    <MenuItem key={g.ID_GRUPO} value={g.ID_GRUPO}>
                      {g.GRUPO}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ width: 160, ...fin }}>
                <InputLabel>{t("Samples.subgroup")}</InputLabel>
                <Select
                  label={t("Samples.subgroup")}
                  value={esc.ESCANDALLO_SUBGRUPO || ""}
                  sx={fin}
                  onChange={(e) =>
                    update(i, "ESCANDALLO_SUBGRUPO", e.target.value)
                  }
                >
                  {esc.SUBGRUPOS_LIST?.map((sg) => (
                    <MenuItem key={sg.ID_SUBGRUPO} value={sg.ID_SUBGRUPO}>
                      {sg.SUBGRUPO}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ width: 160, ...fin }}>
                <InputLabel>{t("Samples.Supplier")}</InputLabel>
                <Select
                  label={t("Samples.Supplier")}
                  value={esc.PROVEEDOR_ESCANDALLO || ""}
                  sx={fin}
                  onChange={(e) =>
                    update(i, "PROVEEDOR_ESCANDALLO", e.target.value)
                  }
                >
                  {tiposProveedores.map((p) => (
                    <MenuItem key={p.CODPROV} value={p.CODPROV}>
                      {p.N_PROVEEDOR}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                size="small"
                label={t("Samples.units")}
                type="number"
                value={esc.UN || ""}
                onChange={(e) => update(i, "UN", e.target.value)}
                sx={{
                  ...fin,
                  width: 80,
                  "& input[type=number]": {
                    MozAppearance: "textfield",
                  },
                  "& input[type=number]::-webkit-outer-spin-button": {
                    WebkitAppearance: "none",
                    margin: 0,
                  },
                  "& input[type=number]::-webkit-inner-spin-button": {
                    WebkitAppearance: "none",
                    margin: 0,
                  },
                }}
              />

              <TextField
                size="small"
                label={t("Samples.cost")}
                type="number"
                value={esc.COSTE || ""}
                onChange={(e) => update(i, "COSTE", e.target.value)}
                sx={{
                  ...fin,
                  width: 80,
                  "& input[type=number]": {
                    MozAppearance: "textfield",
                  },
                  "& input[type=number]::-webkit-outer-spin-button": {
                    WebkitAppearance: "none",
                    margin: 0,
                  },
                  "& input[type=number]::-webkit-inner-spin-button": {
                    WebkitAppearance: "none",
                    margin: 0,
                  },
                }}
              />

              <IconButton
                color="error"
                size="small"
                onClick={() => askDelete(i)}
                sx={{
                  ml: 0.25,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  "&:hover": {
                    bgcolor: "action.hover",
                  },
                }}
              >
                <DeleteOutlinedIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </AccordionDetails>
      </Accordion>

      {/* ----------------------------
          DIALOGO DE CONFIRMACIÓN
          ---------------------------- */}
      <Dialog open={openDialog} onClose={cancelDelete}>
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          {t("Samples.deleteEscandalloTitle")}
        </DialogTitle>

        <DialogContent>
          <Typography>{t("Samples.deleteEscandalloBody")}</Typography>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={cancelDelete} variant="outlined">
            {t("Samples.cancel")}
          </Button>

          <Button
            onClick={confirmDelete}
            variant="contained"
            color="error"
            sx={{ color: "white" }}
          >
            {t("Samples.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
